import PropTypes from 'prop-types';
import React, { lazy, useMemo, useState } from 'react';
import { pageUrls } from 'globalconfig/constants/keys';
import { hasRestrictedRole } from 'globalutils/fmUtils';
import InsightsIcon from '@material-ui/icons/InsertChart';
import { debounce, isEmpty, isObject } from 'lodash';
import moment from 'moment';
import 'rc-tooltip/assets/bootstrap.css';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { fleetIconMapping } from './constants';
import { useTranslation } from 'react-i18next';
import { FleetScoreUI } from './utils/uiRenderers/FleetScoreUI';
import { DriverBehaviour } from './utils/uiRenderers/DriverBehaviour';
import { UnpoweredDays } from './utils/uiRenderers/UnPoweredDays';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Label = lazy(() => import(`globalcomponents/UI/Label/${layout}`));
const MainFilter = lazy(() => import(`./MainFilter`));
const InfoCard = lazy(() => import(`./InfoCard`));

const Component = ({ Styled, scoreCard, companyId, fetchDetails, ...rest }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState();

  const handleFilterChange = (each) => {
    setFilterValue(each);
    fetchDetails({ data: { id: companyId, ...(each?.value === "YEARLY" ? { isYearly: true } : {}) }, apiId: 'getData' });
  };

  const scoreCardItemDescription = {
    actualScore: t('scorecard.actualScore'),
    distractedDriving: t('scorecard.distractedDriving'),
  };

  const guageScore = useMemo(() => {
    const fleetScore = scoreCard?.fleetScore || 0;

    if (fleetScore < 70) {
      return fleetScore / 2.8;
    } else if (fleetScore < 80) {
      return (fleetScore - 70) * 2.5 + 25;
    } else if (fleetScore < 90) {
      return (fleetScore - 80) * 2.5 + 50;
    } else if (fleetScore < 100) {
      return (fleetScore - 90) * 2.5 + 75;
    }

    return fleetScore;
  }, [scoreCard?.fleetScore]);

  const handleClick = debounce((event) => {
    if (!hasRestrictedRole(['FLMANAGER', 'ADMIN'])) {
      const endTime = moment().valueOf();
      const startTime =
        filterValue?.value === "YEARLY"
          ? moment().subtract(1, 'year').valueOf()
          : moment().subtract(30, 'days').valueOf();
      history.push(`${pageUrls?.cameraList}?endTime=${endTime}&startTime=${startTime}`);
    }
  }, 100);

  if (isEmpty(scoreCard) && isObject(scoreCard)) {
    return (
      <Styled.MainWrapper>
        <Styled.FilterWrapper>
          <div className="filter">
            <MainFilter onChange={handleFilterChange} />
          </div>
        </Styled.FilterWrapper>
        <Styled.Wrapper widthOfProgress={90}>
          <div className="noDataFound">
            <InsightsIcon color="secondary" className="icon" />
            <Label variant="h1" className="text">
              {t('scorecard.noDataText')}
            </Label>
          </div>
        </Styled.Wrapper>
      </Styled.MainWrapper>
    );
  }

  if (!scoreCard) {
    return <></>;
  }

  return (
    <Styled.MainWrapper>
      <Styled.FilterWrapper>
        <div className="filter">
          <MainFilter onChange={handleFilterChange} />
        </div>
        <div className="info">
          <InfoCard
            fleetIconMapping={fleetIconMapping}
            companyName={scoreCard?.companyName}
            totalCount={scoreCard?.totalVehicleCount}
          />
        </div>
      </Styled.FilterWrapper>
      <Styled.Wrapper widthOfProgress={90}>
        <div className="left">
          <FleetScoreUI guageScore={guageScore} scoreCard={scoreCard} />
        </div>
        <div className="right">
          <DriverBehaviour Styled={Styled} scoreDes={scoreCardItemDescription} handleClick={handleClick} scoreCard={scoreCard} />
          <UnpoweredDays scoreCard={scoreCard} scoreDes={scoreCardItemDescription} Styled={Styled} />
        </div>
      </Styled.Wrapper>
    </Styled.MainWrapper>
  );
};

export default Component;

Component.propTypes = {
  history: PropTypes.object.isRequired,
  callback: PropTypes.func.isRequired,
  scoreCard: PropTypes.object.isRequired,
};